import React from 'react';
import { Tab, Tabs, Heading3, Heading4 } from '@origin-digital/style-guide';
import { Box } from '@origin-digital/layout';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import Grid from '../../../../../../../components/MuiGrid/Grid';
import { CalculatorFormValues } from 'apps/SolarCalculator/components/CalculatorForm/CalculatorForm.types';
import { interaction } from '@origin-digital/reporting-client';

const useStyles = makeStyles({
  bulletPoints: {
    color: '#505050',
    fontSize: '14px',
    paddingLeft: '1.3em'
  },
  image: {
    height: 270,
    width: '100%'
  }
});

interface IPackageResultConfig {
  inverterImage: string;
  panelImage: string;
  title: string;
  packageName: string;
  panelTypeName: string;
  inverterTypeName: string;
  inverterBulletPoints: string[];
  panelBulletPoints: string[];
}

const PackageResultConfig: IPackageResultConfig[] = [
  {
    inverterBulletPoints: [
      'Cost effective',
      'Simple design',
      '15 Amp max output',
      "10-Year manufacturer's warranty on parts and 5 years on labour"
    ],
    inverterImage:
      'https://www.originenergy.com.au/content/dam/origin/residential/solar/products/goodwe_inverter_2.png',
    inverterTypeName: 'GoodWe',
    packageName: 'advantage',
    panelBulletPoints: [
      'Half cell for better performance when shaded',
      'Monocrystalline module',
      '19.6% efficiency',
      '12 year material and workmanship warranty. 25 year output warranty'
    ],
    panelImage:
      'https://www.originenergy.com.au/content/dam/origin/residential/solar/JAM60S10_330W_Black-Frame.png',
    panelTypeName: 'JA Solar 330w',
    title: 'Advantage'
  },
  {
    inverterBulletPoints: [
      'Smart grid ready',
      'European design',
      '31.9 Amp max output',
      '10 year parts, 5 year labour warranties'
    ],
    inverterImage:
      'https://www.originenergy.com.au/content/dam/origin/residential/solar/products/square_origin_solar-inverter_fronius-symo.png',
    inverterTypeName: 'Fronius',
    packageName: 'premium',
    panelBulletPoints: [
      'Half cell for better performance when shaded',
      'Monocrystalline module',
      '19.6% efficiency',
      '12 year material and workmanship warranty. 25 year output warranty'
    ],
    panelImage:
      'https://www.originenergy.com.au/content/dam/origin/residential/solar/JAM60S10_330W_Black-Frame.png',
    panelTypeName: 'JA Solar 330w',
    title: 'Premium'
  },
  {
    inverterBulletPoints: [
      'Smart grid ready',
      'European design',
      '31.9 Amp max output',
      '10 year parts, 5 year labour warranties'
    ],
    inverterImage:
      'https://www.originenergy.com.au/content/dam/origin/residential/solar/products/square_origin_solar-inverter_fronius-symo.png',
    inverterTypeName: 'Fronius',
    packageName: 'premiumPlus',
    panelBulletPoints: [
      'Our highest power output',
      'Subtle black appearance',
      'Monocrystalline module',
      '20.2% efficiency',
      '12 year material and workmanship warranty. 25 year output warranty'
    ],
    panelImage:
      'https://www.originenergy.com.au/content/dam/origin/residential/solar/products/square_origin_solar-panel_trina-solar_370w.jpg',
    panelTypeName: 'Trina 370w',
    title: 'Premium Plus'
  }
];

interface IPackageResultProps {
  packageName: string;
  values: CalculatorFormValues;
  submit: (values: CalculatorFormValues, from?: string) => Promise<void>;
}

export const PackagesResult = (props: IPackageResultProps) => {
  const theme = useTheme();
  const classes = useStyles({});
  const matchesSmallBreakpoints = useMediaQuery(theme.breakpoints.up('sm'));
  const matchesXSmallBreakpoints = useMediaQuery(theme.breakpoints.up('xs'));
  const { packageName, values, submit } = props;

  const changePackage = (pName: string) => {
    values.package = pName;
    return values;
  };

  return (
    <Box>
      <Box paddingX={8}>
        <Heading3 text="2. Package Information" />
      </Box>
      <Tabs defaultActiveTab={packageName} flexMode="allMedia">
        {PackageResultConfig.map((p, i) => (
          <Tab
            id={p.packageName}
            title={p.title}
            onClick={() => {
              changePackage(p.packageName);
              submit(values, 'packageCall');
              interaction('click', {
                componentType: 'tab',
                id: 'package-result-solar-calc',
                parentContainer: 'TBC',
                appName: 'solar-calc',
                label: p.packageName.charAt(0).toUpperCase() + p.packageName.slice(1)
              });
            }}
            key={i}
          >
            <Box paddingX={8}>
              <Grid
                container
                alignItems="center"
                justify="center"
                className="package-result-image-grid-ie"
              >
                <Grid item xs={4} sm={4} md={4} lg={4} xl={4}>
                  <img
                    className={classes.image}
                    src={p.panelImage}
                    alt="solar-panel"
                    style={{
                      height: matchesSmallBreakpoints
                        ? '230px'
                        : matchesXSmallBreakpoints && '140px'
                    }}
                  />
                </Grid>
                <Grid alignItems="center" justify="center" item xs={2} sm={2} md={2} lg={2} xl={2}>
                  <h1>+</h1>
                </Grid>
                <Grid item xs={4} sm={4} md={4} lg={4} xl={4}>
                  <img
                    className={classes.image}
                    src={p.inverterImage}
                    alt="solar-inverter"
                    style={{
                      height: matchesSmallBreakpoints
                        ? '230px'
                        : matchesXSmallBreakpoints && '140px'
                    }}
                  />
                </Grid>
              </Grid>
              <Grid
                container
                direction="row"
                justify="space-between"
                className="package-result-content-grid-ie"
              >
                <Grid item container xs={5} direction="column">
                  <Heading4 text={p.panelTypeName} />
                  <ul className={`${classes.bulletPoints} package-result-content-item1-ie`}>
                    {p.panelBulletPoints.map((b, k) => (
                      <li key={k}>{b}</li>
                    ))}
                  </ul>
                </Grid>
                <Grid item xs={5}>
                  <Heading4 text={p.inverterTypeName} />
                  <ul className={classes.bulletPoints}>
                    {p.inverterBulletPoints.map((inv, w) => (
                      <li key={w}>{inv}</li>
                    ))}
                  </ul>
                </Grid>
              </Grid>
            </Box>
          </Tab>
        ))}
      </Tabs>
    </Box>
  );
};
